import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUserRole } from '@/hooks/useUserRole';
import { useToast } from '@/hooks/use-toast';
import { useMutation, useQuery } from '@tanstack/react-query';
import { triggerIntegrationSync, SOURCE_TO_INTEGRATION_TYPE } from '@/lib/integrationSync';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Loader2, History, FileCheck, Gavel, FileText, Plug, RotateCcw, Search, X, Download,
  ChevronDown, ArrowRight, ArrowUpDown,
} from 'lucide-react';
import { useLocalization } from '@/hooks/useLocalization';

interface TimelineEvent {
  event_id: string;
  source: 'ai_output_review' | 'decision_audit' | 'ai_output_audit' | 'integration_audit';
  actor_user_id: string;
  actor_email: string | null;
  action: string;
  previous_status: string | null;
  new_status: string | null;
  notes: string | null;
  entity_title: string | null;
  created_at: string;
}

const SOURCE_CONFIG: Record<TimelineEvent['source'], { label: string; icon: React.ElementType; tint: string }> = {
  ai_output_review: { label: 'AI Review', icon: FileCheck, tint: 'bg-primary/10 text-primary' },
  decision_audit: { label: 'Decision', icon: Gavel, tint: 'bg-secondary text-foreground' },
  ai_output_audit: { label: 'AI Report', icon: FileText, tint: 'bg-primary/10 text-primary' },
  integration_audit: { label: 'Integration', icon: Plug, tint: 'bg-muted text-muted-foreground' },
};

type GroupBy = 'none' | 'day' | 'source' | 'actor' | 'entity';
type SortBy = 'newest' | 'oldest' | 'actor' | 'source';
type Range = '24h' | '7d' | '30d' | 'all';
const PAGE = 25;

const RANGE_MS: Record<Exclude<Range, 'all'>, number> = { '24h': 864e5, '7d': 7 * 864e5, '30d': 30 * 864e5 };
const humanize = (v: string) => v.replace(/_/g, ' ');

// Semantic colour for the status an event moved something into.
const statusTone = (status: string | null) => {
  const v = (status ?? '').toLowerCase();
  if (/(approv|publish|accept|complete|resolved|success|generated)/.test(v)) return 'bg-success/10 text-success';
  if (/(reject|fail|delet|error|escalat|revok)/.test(v)) return 'bg-destructive/10 text-destructive';
  if (/(pending|review|draft|more_evidence|progress)/.test(v)) return 'bg-warning/15 text-warning-foreground';
  return 'bg-muted text-muted-foreground';
};

const dayLabel = (iso: string) => {
  const d = new Date(iso);
  const today = new Date();
  const yest = new Date();
  yest.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yest.toDateString()) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
};

const GovernanceTimeline = () => {
  const { tx, t } = useLocalization();
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<TimelineEvent | null>(null);
  const { isPMO } = useUserRole();
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await supabase.rpc('get_governance_timeline', { p_limit: 500 });
    if (fetchError) {
      setError(fetchError.message);
    } else {
      setEvents((data ?? []) as TimelineEvent[]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Active integration connections for this tenant, used to resolve a
  // deleted record's source back to the connection that can re-sync it.
  const { data: integrationConfigs } = useQuery({
    queryKey: ['integration-configs-for-restore'],
    queryFn: async () => {
      const { data, error: fetchError } = await supabase
        .from('integration_configs')
        .select('id, integration_type');
      if (fetchError) throw fetchError;
      return data;
    },
    enabled: isPMO,
  });

  const restoreMutation = useMutation({
    mutationFn: async (event: TimelineEvent) => {
      const source = event.entity_title;
      const integrationType = source ? SOURCE_TO_INTEGRATION_TYPE[source] : undefined;
      const matches = integrationType
        ? (integrationConfigs ?? []).filter((c) => c.integration_type === integrationType)
        : [];

      if (matches.length === 0) {
        throw new Error(`No active "${source}" connection found — reconnect the integration first, then try again.`);
      }

      const results = await Promise.allSettled(
        matches.map((c) => triggerIntegrationSync(c.id, c.integration_type)),
      );
      const failed = results.filter((r) => r.status === 'rejected');
      if (failed.length === results.length) {
        throw new Error('Sync failed for every matching connection.');
      }
      return { total: results.length, failed: failed.length };
    },
    onSuccess: (result) => {
      toast({
        title: t('Restore sync triggered'),
        description: result.failed > 0
          ? t('{0}/{1} connection(s) synced — check the Integrations page for details.', { 0: result.total - result.failed, 1: result.total })
          : t('If the record still exists in the source system, it will reappear shortly. If it was deleted there too, nothing will come back.'),
      });
    },
    onError: (err: Error) => {
      toast({ title: t('Restore failed'), description: err.message, variant: 'destructive' });
    },
  });

  // ---- filters / sort / group ------------------------------------------------
  const [query, setQuery] = useState('');
  const [sources, setSources] = useState<Set<TimelineEvent['source']>>(new Set());
  const [actionFilter, setActionFilter] = useState('all');
  const [actorFilter, setActorFilter] = useState('all');
  const [range, setRange] = useState<Range>('all');
  const [sortBy, setSortBy] = useState<SortBy>('newest');
  const [groupBy, setGroupBy] = useState<GroupBy>('day');
  const [visible, setVisible] = useState(PAGE);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const actions = useMemo(() => Array.from(new Set(events.map((e) => e.action))).sort(), [events]);
  const actors = useMemo(
    () => Array.from(new Set(events.map((e) => e.actor_email ?? 'Unknown user'))).sort(),
    [events],
  );
  const sourceCounts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const e of events) c[e.source] = (c[e.source] ?? 0) + 1;
    return c;
  }, [events]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const cutoff = range === 'all' ? 0 : Date.now() - RANGE_MS[range];
    const list = events.filter((e) => {
      if (sources.size && !sources.has(e.source)) return false;
      if (actionFilter !== 'all' && e.action !== actionFilter) return false;
      if (actorFilter !== 'all' && (e.actor_email ?? 'Unknown user') !== actorFilter) return false;
      if (cutoff && new Date(e.created_at).getTime() < cutoff) return false;
      if (q) {
        const hay = `${e.action} ${e.entity_title ?? ''} ${e.actor_email ?? ''} ${e.notes ?? ''} ${e.new_status ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const t = (e: TimelineEvent) => new Date(e.created_at).getTime();
    list.sort((a, b) => {
      switch (sortBy) {
        case 'oldest': return t(a) - t(b);
        case 'actor': return (a.actor_email ?? '').localeCompare(b.actor_email ?? '') || t(b) - t(a);
        case 'source': return a.source.localeCompare(b.source) || t(b) - t(a);
        default: return t(b) - t(a);
      }
    });
    return list;
  }, [events, query, sources, actionFilter, actorFilter, range, sortBy]);

  const shown = filtered.slice(0, visible);

  const groups = useMemo(() => {
    const keyOf = (e: TimelineEvent) => {
      switch (groupBy) {
        case 'day': return dayLabel(e.created_at);
        case 'source': return SOURCE_CONFIG[e.source].label;
        case 'actor': return e.actor_email ?? 'Unknown user';
        case 'entity': return e.entity_title ?? 'No linked record';
        default: return 'All events';
      }
    };
    const map = new Map<string, TimelineEvent[]>();
    for (const e of shown) {
      const k = keyOf(e);
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(e);
    }
    return Array.from(map.entries());
  }, [shown, groupBy]);

  const activeFilters = sources.size + (actionFilter !== 'all' ? 1 : 0) + (actorFilter !== 'all' ? 1 : 0) + (range !== 'all' ? 1 : 0) + (query ? 1 : 0);
  const resetFilters = () => {
    setQuery(''); setSources(new Set()); setActionFilter('all'); setActorFilter('all'); setRange('all'); setVisible(PAGE);
  };
  const toggleSource = (src: TimelineEvent['source']) => {
    setVisible(PAGE);
    setSources((prev) => {
      const next = new Set(prev);
      if (next.has(src)) next.delete(src); else next.add(src);
      return next;
    });
  };
  const toggleGroup = (k: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });

  const exportCsv = () => {
    const esc = (v: string | null) => `"${(v ?? '').replace(/"/g, '""')}"`;
    const rows = [['When', 'Source', 'Action', 'Record', 'From', 'To', 'Actor', 'Notes'].join(',')].concat(
      filtered.map((e) =>
        [esc(e.created_at), esc(SOURCE_CONFIG[e.source].label), esc(humanize(e.action)), esc(e.entity_title),
          esc(e.previous_status), esc(e.new_status), esc(e.actor_email), esc(e.notes)].join(','),
      ),
    );
    const url = URL.createObjectURL(new Blob([rows.join('\n')], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-trail-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <History className="w-5 h-5 text-primary" />
              <CardTitle>{t('Governance Timeline')}</CardTitle>
            </div>
            <CardDescription className="mt-1">
              
              {t('Detection, analysis, review and decision in one feed')}
              {events.length > 0 && t(' · {0} of {1} events', { 0: filtered.length, 1: events.length })}
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={exportCsv} disabled={filtered.length === 0}>
            <Download className="w-3.5 h-3.5" />
            
            {t('Export CSV')}
          </Button>
        </div>

        {/* Source chips double as counts and quick filters */}
        <div className="flex flex-wrap gap-2 pt-3">
          {(Object.keys(SOURCE_CONFIG) as TimelineEvent['source'][]).map((src) => {
            const c = SOURCE_CONFIG[src];
            const Icon = c.icon;
            const on = sources.has(src);
            return (
              <button
                key={src}
                type="button"
                aria-pressed={on}
                onClick={() => toggleSource(src)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  on ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card text-foreground hover:bg-secondary',
                )}
              >
                <Icon className="w-3.5 h-3.5" />
                {tx(c.label)}
                <span className={cn('tabular-nums', on ? 'text-primary-foreground/80' : 'text-muted-foreground')}>
                  {sourceCounts[src] ?? 0}
                </span>
              </button>
            );
          })}
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2 pt-2">
          <div className="relative min-w-[200px] flex-1">
            <Search className="pointer-events-none absolute start-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => { setQuery(e.target.value); setVisible(PAGE); }}
              placeholder={t('Search action, record, person, notes')}
              aria-label={t('Search audit events')}
              className="h-9 ps-8"
            />
          </div>
          <Select value={range} onValueChange={(v) => { setRange(v as Range); setVisible(PAGE); }}>
            <SelectTrigger className="h-9 w-[130px]" aria-label={t('Date range')}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('All time')}</SelectItem>
              <SelectItem value="24h">{t('Last 24 hours')}</SelectItem>
              <SelectItem value="7d">{t('Last 7 days')}</SelectItem>
              <SelectItem value="30d">{t('Last 30 days')}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setVisible(PAGE); }}>
            <SelectTrigger className="h-9 w-[150px]" aria-label={t('Filter by action')}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('All actions')}</SelectItem>
              {actions.map((a) => <SelectItem key={a} value={a} className="capitalize">{humanize(a)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={actorFilter} onValueChange={(v) => { setActorFilter(v); setVisible(PAGE); }}>
            <SelectTrigger className="h-9 w-[160px]" aria-label={t('Filter by person')}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('Everyone')}</SelectItem>
              {actors.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <ArrowUpDown className="w-3.5 h-3.5" />
            <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortBy)}>
              <SelectTrigger className="h-8 w-[140px] text-xs" aria-label={t('Sort by')}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">{t('Newest first')}</SelectItem>
                <SelectItem value="oldest">{t('Oldest first')}</SelectItem>
                <SelectItem value="actor">{t('Person (A-Z)')}</SelectItem>
                <SelectItem value="source">{t('Source')}</SelectItem>
              </SelectContent>
            </Select>
            <span>{t('Group')}</span>
            <Select value={groupBy} onValueChange={(v) => { setGroupBy(v as GroupBy); setCollapsed(new Set()); }}>
              <SelectTrigger className="h-8 w-[130px] text-xs" aria-label={t('Group by')}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="day">{t('By day')}</SelectItem>
                <SelectItem value="source">{t('By source')}</SelectItem>
                <SelectItem value="actor">{t('By person')}</SelectItem>
                <SelectItem value="entity">{t('By record')}</SelectItem>
                <SelectItem value="none">{t('No grouping')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {activeFilters > 0 && (
            <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs" onClick={resetFilters}>
              <X className="w-3.5 h-3.5" />
              
              {t('Clear')} {activeFilters}  {t('filter')}{activeFilters === 1 ? '' : 's'}
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : events.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">{t('No governance events recorded yet.')}</p>
        ) : filtered.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            
            {t('No events match these filters.')}
            <div className="mt-2"><Button variant="outline" size="sm" onClick={resetFilters}>{t('Clear filters')}</Button></div>
          </div>
        ) : (
          <div className="max-h-[640px] overflow-y-auto rounded-lg border border-border">
            {groups.map(([label, items]) => {
              const isCollapsed = collapsed.has(label);
              return (
                <section key={label}>
                  {groupBy !== 'none' && (
                    <button
                      type="button"
                      onClick={() => toggleGroup(label)}
                      aria-expanded={!isCollapsed}
                      className="sticky top-0 z-10 flex w-full items-center justify-between border-b border-border bg-secondary/80 px-4 py-2 text-start text-xs font-semibold uppercase tracking-wide text-muted-foreground backdrop-blur"
                    >
                      <span className="truncate">{tx(label)}</span>
                      <span className="flex items-center gap-2 font-normal normal-case">
                        {items.length}
                        <ChevronDown className={cn('h-4 w-4 transition-transform', isCollapsed && '-rotate-90')} />
                      </span>
                    </button>
                  )}
                  {!isCollapsed && (
                    <ul className="divide-y divide-border">
                      {items.map((e) => {
                        const config = SOURCE_CONFIG[e.source];
                        const Icon = config.icon;
                        const isRestorableDelete = e.source === 'integration_audit' && e.action === 'synced_record_deleted';
                        return (
                          <li key={`${e.source}-${e.event_id}`} className="flex items-start gap-3 px-4 py-3 hover:bg-muted/30 transition-colors">
                            <span className={cn('mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg', config.tint)}>
                              <Icon className="w-3.5 h-3.5" />
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                <span className="text-sm font-medium text-foreground capitalize">{humanize(e.action)}</span>
                                {e.new_status && (
                                  <span className="inline-flex items-center gap-1 text-[11px]">
                                    {e.previous_status && (
                                      <>
                                        <span className="rounded bg-muted px-1.5 py-px text-muted-foreground capitalize">{humanize(e.previous_status)}</span>
                                        <ArrowRight className="h-3 w-3 text-muted-foreground" />
                                      </>
                                    )}
                                    <span className={cn('rounded px-1.5 py-px font-medium capitalize', statusTone(e.new_status))}>{humanize(e.new_status)}</span>
                                  </span>
                                )}
                                {groupBy !== 'source' && <Badge variant="outline" className="text-[10px] font-normal">{tx(config.label)}</Badge>}
                              </div>
                              {e.entity_title && <p className="truncate text-sm text-muted-foreground">{e.entity_title}</p>}
                              <p className="mt-0.5 text-xs text-muted-foreground">
                                {groupBy !== 'actor' && <>{e.actor_email ?? t('Unknown user')} · </>}
                                {groupBy === 'day'
                                  ? new Date(e.created_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
                                  : new Date(e.created_at).toLocaleString()}
                              </p>
                              {e.notes && <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{e.notes}</p>}
                            </div>
                            {isRestorableDelete && isPMO && (
                              <Button variant="outline" size="sm" className="gap-1.5 shrink-0" onClick={() => setRestoreTarget(e)}>
                                <RotateCcw className="w-3.5 h-3.5" />
                                
                                {t('Restore')}
                              </Button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </section>
              );
            })}
            {filtered.length > shown.length && (
              <div className="border-t border-border p-3 text-center">
                <Button variant="ghost" size="sm" onClick={() => setVisible((v) => v + PAGE)}>
                  
                  {t('Show')} {Math.min(PAGE, filtered.length - shown.length)}  {t('more (')}{filtered.length - shown.length}  {t('remaining)')}
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>

      <AlertDialog open={!!restoreTarget} onOpenChange={(open) => { if (!open) setRestoreTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('Restore this deleted record?')}</AlertDialogTitle>
            <AlertDialogDescription>
              
              {t('This triggers a re-sync of "')}{restoreTarget?.entity_title}{t('". If the record still exists in the source system, it will reappear here. If it was also deleted there, nothing will come back — this doesn\'t recreate data out of nothing, it just re-pulls whatever the source currently has.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setRestoreTarget(null)}>{t('Cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!restoreTarget) return;
                restoreMutation.mutate(restoreTarget);
                setRestoreTarget(null);
              }}
            >
              
              {t('Restore')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default GovernanceTimeline;
